import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ConsultingProvider } from './context/ConsultingContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { ToastProvider } from './context/ToastContext';
import { initGA, logPageView } from './utils/analytics';
import { setupErrorTracking } from './utils/errorTracking';
import { useMediaQuery } from './hooks/useMediaQuery'; // useMediaQuery 추가

// 새로운 경로로 import 업데이트
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/user/DashboardPage';
import ConsultingPage from './pages/user/ConsultingPage';
import ResumePage from './pages/user/ResumePage';
import ResumeNewPage from './pages/user/ResumeNewPage';
import MobileResumePage from './pages/user/MobileResumePage'; // 모바일 이력서 페이지는 유지
import DocumentsPage from './pages/user/DocumentsPage';
import CoverLetterPage from './pages/user/CoverLetterPage';
import UserGuidePage from './pages/user/UserGuidePage';
import PortfolioPage from './pages/user/PortfolioPage';
import Profile from './pages/profile/Profile';
import PromptManager from './pages/admin/PromptManager';
import UserManager from './pages/admin/UserManager';
import UserResumeManager from './pages/admin/UserResumeManager';
import PromoCodeAdmin from './pages/admin/PromoCodeAdmin';
import PromoCodeRequests from './pages/admin/PromoCodeRequests';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsPage from './pages/legal/TermsPage';
import PricingPage from './pages/PricingPage';
import BrochurePage from './pages/BrochurePage';
import PamphletPage from './pages/PamphletPage';

// 블로그 페이지 import
import BlogListPage from './pages/blog/BlogListPage';
import BlogDetailPage from './pages/blog/BlogDetailPage';
import BlogEditorPage from './pages/blog/BlogEditorPage';

import { ProtectedRoute, AdminProtectedRoute, PromoCodeProtectedRoute } from './components/auth';
import { SlugRedirect } from './components/routing';
import RouteTracker from './components/RouteTracker';
import { SunsetBanner } from './components/common';
import ErrorBoundary from './components/common/ErrorBoundary';
import { FloatingInquiryButton } from './components/common';
import './App.css';

// 반응형 컴포넌트들
const ResponsiveResumePage = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  return isMobile ? <MobileResumePage /> : <ResumePage />;
};

// 데스크탑 버전만 사용하도록 변경
const ResponsiveDashboardPage = () => {
  // const isMobile = useMediaQuery('(max-width: 768px)');
  // return isMobile ? <MobileDashboardPage /> : <DashboardPage />;
  return <DashboardPage />; // 항상 데스크탑 버전 사용
};

const ResponsiveDocumentsPage = () => {
  // const isMobile = useMediaQuery('(max-width: 768px)');
  // return isMobile ? <MobileDocumentsPage /> : <DocumentsPage />;
  return <DocumentsPage />; // 항상 데스크탑 버전 사용
};

// FloatingInquiryButton을 조건부로 렌더링하는 컴포넌트
const ConditionalFloatingButton = () => {
  const location = useLocation();
  const isPortfolioPage = location.pathname.startsWith('/portfolio/');
  
  // 포트폴리오 페이지가 아닐 때만 플로팅 버튼 표시
  return !isPortfolioPage ? <FloatingInquiryButton /> : null;
};

function App() {
  // GA4 초기화
  useEffect(() => {
    initGA();
    setupErrorTracking(); // 에러 추적 설정
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <ToastProvider>
            <Router future={{ v7_relativeSplatPath: true }}>
              <OnboardingProvider>
                <ConsultingProvider>
                  <div className="App">
                <RouteTracker />
                <SunsetBanner />
                <ConditionalFloatingButton />
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ResponsiveDashboardPage />} />
              
              <Route path="/portfolio/:slug" element={<PortfolioPage />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <ResponsiveDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/consulting" 
                element={
                  <ProtectedRoute>
                    <PromoCodeProtectedRoute>
                      <ConsultingPage />
                    </PromoCodeProtectedRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/documents" 
                element={
                  <ProtectedRoute>
                    <PromoCodeProtectedRoute>
                      <ResponsiveDocumentsPage />
                    </PromoCodeProtectedRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/resume/new" 
                element={
                  <ProtectedRoute>
                    <PromoCodeProtectedRoute>
                      <ResumeNewPage />
                    </PromoCodeProtectedRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/resume/:id?" 
                element={
                  <ProtectedRoute>
                    <PromoCodeProtectedRoute>
                      <ResponsiveResumePage />
                    </PromoCodeProtectedRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/cover-letter/:id?" 
                element={
                  <ProtectedRoute>
                    <PromoCodeProtectedRoute>
                      <CoverLetterPage />
                    </PromoCodeProtectedRoute>
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/guide" 
                element={<UserGuidePage />} 
              />

              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/privacy" 
                element={<PrivacyPolicyPage />} 
              />
              
              <Route 
                path="/terms" 
                element={<TermsPage />} 
              />
              <Route 
                path="/pricing" 
                element={<PricingPage />} 
              />
              
              <Route 
                path="/brochure" 
                element={<BrochurePage />} 
              />
              
              <Route 
                path="/pamphlet" 
                element={<PamphletPage />} 
              />

              {/* 블로그 라우트 */}
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/new" element={
                <AdminProtectedRoute>
                  <BlogEditorPage />
                </AdminProtectedRoute>
              } />
              <Route path="/blog/edit/:id" element={
                <AdminProtectedRoute>
                  <BlogEditorPage />
                </AdminProtectedRoute>
              } />
              <Route path="/blog/:id" element={<BlogDetailPage />} />

              <Route 
                path="/admin-prompts" 
                element={
                  <AdminProtectedRoute>
                    <PromptManager />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin-users" 
                element={
                  <AdminProtectedRoute>
                    <UserManager />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin-users/:userId/resumes" 
                element={
                  <AdminProtectedRoute>
                    <UserResumeManager />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin-promo-codes" 
                element={
                  <AdminProtectedRoute>
                    <PromoCodeAdmin />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin-promo-requests" 
                element={
                  <AdminProtectedRoute>
                    <PromoCodeRequests />
                  </AdminProtectedRoute>
                } 
              />
              
              {/* 포트폴리오와 기타 슬러그 처리 - 마지막에 배치 */}
              <Route path="/:slug" element={<SlugRedirect />} />
              </Routes>
                  </div>
                </ConsultingProvider>
              </OnboardingProvider>
            </Router>
          </ToastProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;